import type { Entity } from '@backstage/catalog-model';
import type { KeyRolesFields } from '@ipen-portal/plugin-key-roles-common';

import {
  isChapterEntity,
  isOrganisationDsoEntity,
  isOrganisationEntity,
  isSquadEntity,
} from '@ipen-portal/ipen-entity-model';
import { notEmpty } from '@ipen-portal/plugin-common';
import { useMemoValue } from '@ipen-portal/plugin-common-react';
import { keyRoles } from '@ipen-portal/plugin-key-roles-common';
import { useMemo } from 'react';

import type { EntityKeyRoleData } from '../types/EntityKeyRoleData';

const FieldIconMapping: Partial<Record<KeyRolesFields, {
  icon: string;
  matcher: (entity: Entity) => boolean;
}[]>> = {
  parent: [
    {
      icon: 'ic:baseline-business',
      matcher: isOrganisationDsoEntity,
    },
    {
      icon: 'ic:baseline-business',
      matcher: isOrganisationEntity,
    },
    {
      icon: 'icomoon-free:make-group',
      matcher: isSquadEntity,
    },
    {
      icon: 'icomoon-free:make-group',
      matcher: isChapterEntity,
    },
  ],
};

export const useKeyRoles = (
  entity?: Entity | null,
  option?: {
    exclude?: KeyRolesFields[];
    include?: KeyRolesFields[];
    skipEmpty?: boolean;
  },
) => {
  const normalizedOptions = useMemoValue(option);
  return useMemo(() => {
    if (!entity) {
      return [];
    }
    const { exclude, include, skipEmpty } = normalizedOptions ?? {};
    const result = keyRoles
      .filter(kr => !exclude?.includes(kr.field))
      .filter(kr => include?.includes(kr.field) ?? true)
      .filter(kr => kr.matcher(entity))
      .map(kr => {
        const ref = kr.extractRef(entity);
        if (skipEmpty && !ref) {
          return;
        }
        const iconOverride = FieldIconMapping[kr.field]?.find(({ matcher }) =>
          matcher(entity),
        )?.icon;
        return {
          entityRef: ref,
          iconOverride,
          roleOptions: kr.options,
        } satisfies EntityKeyRoleData;
      });
    return result.filter(notEmpty);
  }, [entity, normalizedOptions]);
};
